import { Badge, Button, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useEffect, useMemo, useState } from 'react';
import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import TrackerFilterPopover from '../TrackerFilter/TrackerFilterPopover';
import { filterStorageTypeMap, TrackerDataType } from '../../shared/constants/constants';
import { useLocation } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState, UserState } from '../../redux-types';
import { clearFilters, getTrackerFilterFromStoredFilter } from '../TrackerFilter/trackerFilterHelpers';
import * as UserActions from '../Authorization/userSlice';
import { useQuery } from '@tanstack/react-query';
import { getFuzzySearchServices } from '../../shared/requests/serviceNetworkRequests';
import { getFuzzySearchInteractions } from '../../shared/requests/interactionsRequsts';
import { getFuzzySearchEngagements } from '../../shared/requests/engagementsRequest';
import dayjs from '../../utils/dayjs';

interface TrackerTableFilterProps {
  setDataCallback: (data: any) => void;
  eventHandler: any;
  dropdownTable: any;
}

const useStyles = makeStyles({
  fullHeight: {
    height: '100%',
  },
});

export default function TrackerTableFilter(props: TrackerTableFilterProps) {
  const classes = useStyles();
  const { state } = useLocation();

  const { setDataCallback, eventHandler, dropdownTable } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [inProgress, setInProgress] = useState(false);
  const [pollUntil, setPollUntil] = useState<number | null>(null);

  const currentUser = useSelector<StoreState, UserState>((state) => state.user);

  let componentColor: 'mediaInteraction' | 'primary' | 'primaryLighter';
  switch (eventHandler.type) {
    case TrackerDataType.MEDIA_INTERACTION:
      componentColor = 'mediaInteraction';
      break;
    case TrackerDataType.ENGAGEMENT:
      componentColor = 'primaryLighter';
      break;
    default:
      componentColor = 'primary';
      break;
  }

  const filters = useFilters();
  const filtersDispatch = useFiltersDispatch();

  const dispatch = useDispatch();

  const fuzzySearchEnabled = useMemo(
    () => Boolean(state?.queryString && state?.category),
    [state?.queryString, state?.category]
  );
  const filtersDisabled = fuzzySearchEnabled;

  useEffect(() => {
    if (fuzzySearchEnabled) {
      setPollUntil(dayjs().add(10, 'second').valueOf());
    } else {
      setPollUntil(null);
    }
  }, [fuzzySearchEnabled, state?.queryString, state?.category]);

  useEffect(() => {
    if (pollUntil === null) {
      return;
    }
    const now = dayjs().valueOf();
    const remainingMs = pollUntil - now;
    if (remainingMs <= 0) {
      setPollUntil(null);
      return;
    }
    const timeoutId = setTimeout(() => {
      setPollUntil(null);
    }, remainingMs);
    return () => clearTimeout(timeoutId);
  }, [pollUntil]);

  const fuzzySearchQuery = useQuery({
    queryKey: ['fuzzySearch', eventHandler.type, state?.queryString, state?.category],
    queryFn: async () => {
      switch (eventHandler.type) {
        case TrackerDataType.MEDIA_INTERACTION:
          const interactionsData = await getFuzzySearchInteractions(state?.queryString, state?.category);
          return interactionsData || null;
        case TrackerDataType.ENGAGEMENT:
          const engagementsData = await getFuzzySearchEngagements(state?.queryString, state?.category);
          return engagementsData || null;
        default:
          const servicesData = await getFuzzySearchServices(state?.queryString, state?.category);
          return servicesData || null;
      }
    },
    enabled: fuzzySearchEnabled,
    refetchInterval: () => {
      if (!fuzzySearchEnabled || pollUntil === null) {
        return false;
      }
      return dayjs().add(2, 'second').valueOf() < pollUntil ? 2000 : false;
    },
  });

  useEffect(() => {
    if (!fuzzySearchEnabled || !fuzzySearchQuery.isFetching || pollUntil !== null) {
      return;
    }
    setPollUntil(dayjs().add(10, 'second').valueOf());
  }, [fuzzySearchEnabled, fuzzySearchQuery.isFetching, pollUntil]);

  async function clearFiltersOnClick() {
    dispatch(
      UserActions.setUserStoredFilters({
        // @ts-ignore
        [filterStorageTypeMap[eventHandler.type]]: null,
      })
    );
    clearFilters(eventHandler.type);
  }

  const getFilterButtonIconColor = () => {
    const filterKeysCount = Object.keys(filters).length;
    if (filterKeysCount > 0) {
      return componentColor;
    } else {
      return 'whiteScale';
    }
  };

  const handleFiltersButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFiltersPopoverClose = () => {
    setAnchorEl(null);
  };

  const shouldDisplayClearAllButton = () => {
    return Object.keys(filters).length > 0;
  };

  const handleClearAllFilters = () => {
    setDataCallback(null);
    clearFiltersOnClick();
    filtersDispatch({ type: 'setFilters', payload: {} });
  };

  useEffect(() => {
    if (state?.queryString) {
      if (fuzzySearchQuery.isFetching && fuzzySearchQuery.data === undefined) {
        setDataCallback(null);
        return;
      }
      if (!fuzzySearchQuery.isFetching && fuzzySearchQuery.data !== undefined) {
        setDataCallback(fuzzySearchQuery.data);
        return;
      }
    }
    if (fuzzySearchQuery.error) {
      alert(fuzzySearchQuery.error);
      if (fuzzySearchQuery.data === undefined) {
        setDataCallback(null);
      }
    }
  }, [setDataCallback, state?.queryString, fuzzySearchQuery.isFetching, fuzzySearchQuery.data, fuzzySearchQuery.error]);

  useEffect(() => {
    const savedFilters =
      // @ts-ignore
      currentUser.userInfo.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[eventHandler.type]];
    if (savedFilters && dropdownTable) {
      const parsedFilters = getTrackerFilterFromStoredFilter(savedFilters, dropdownTable, eventHandler.type);
      if (JSON.stringify(parsedFilters) === '{}') {
        dispatch(
          UserActions.setUserStoredFilters({
            // @ts-ignore
            [filterStorageTypeMap[eventHandler.type]]: null,
          })
        );
      } else if (filters !== parsedFilters) {
        filtersDispatch({ type: 'setFilters', payload: parsedFilters });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.userInfo.nonPIUserSettings, filtersDispatch, eventHandler.type, dropdownTable]);

  return (
    <>
      <Badge className={classes.fullHeight} badgeContent={Object.keys(filters).length} color="error">
        <Button
          color="whiteScale"
          variant="outlined"
          onClick={handleFiltersButtonClick}
          disabled={filtersDisabled}
          startIcon={<TuneIcon color={getFilterButtonIconColor()} />}
          data-cy="tracker-filter-btn"
        >
          Filters
        </Button>
      </Badge>
      <Tooltip title={'My Entries In Progress'} placement="top" enterDelay={500}>
        <Button
          className={classes.fullHeight}
          color="whiteScale"
          variant="outlined"
          onClick={() => setInProgress(true)}
          disabled={filtersDisabled}
          startIcon={<TaskAltIcon color={getFilterButtonIconColor()} />}
          data-cy="tracker-table-filter-in-progress-btn"
        >
          In Progress
        </Button>
      </Tooltip>
      {shouldDisplayClearAllButton() && (
        <Button
          className={classes.fullHeight}
          variant="text"
          color={componentColor}
          onClick={() => handleClearAllFilters()}
          data-cy="tracker-filter-clear-all-btn"
        >
          Clear All Filters
        </Button>
      )}
      <TrackerFilterPopover
        anchorEl={anchorEl}
        onClose={handleFiltersPopoverClose}
        setDataCallback={setDataCallback}
        eventHandler={eventHandler}
        inProgress={inProgress}
        inProgressCallback={setInProgress}
      />
    </>
  );
}
